export type WrongRecipientMutationContactPoint = {
  contactPointId: string | null
  kind: string
  value: string
  label: string
  customerId: string | null
  propertyId: string | null
}

export type WrongRecipientMutationCustomer = {
  customerId: string
  displayName: string
  role: string
  contactPoints: WrongRecipientMutationContactPoint[]
}

export type NormalizedWrongRecipientReplacementOutput = {
  action: "replace" | "remove"
  affectedCustomer: WrongRecipientMutationCustomer
  replacementCustomer: WrongRecipientMutationCustomer | null
}

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown): JsonObject | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : null

const textValue = (value: unknown): string => (typeof value === "string" ? value.trim() : "")

const optionalText = (value: unknown): string | null => textValue(value) || null

const contactPointSnapshot = (value: unknown): WrongRecipientMutationContactPoint | null => {
  const snapshot = objectValue(value)

  if (!snapshot) return null

  const kind = textValue(snapshot.kind).toUpperCase()
  const pointValue = textValue(snapshot.value)

  if (!kind || !pointValue) return null

  return {
    contactPointId: optionalText(snapshot.contact_point_id),
    kind,
    value: pointValue,
    label: textValue(snapshot.label),
    customerId: optionalText(snapshot.customer_id),
    propertyId: optionalText(snapshot.property_id),
  }
}

const customerSnapshot = (value: unknown): WrongRecipientMutationCustomer | null => {
  const snapshot = objectValue(value)

  if (!snapshot || !Array.isArray(snapshot.contact_points)) return null

  const customerId = textValue(snapshot.customer_id)
  const displayName = textValue(snapshot.display_name)

  if (!customerId || !displayName) return null

  const contactPoints = snapshot.contact_points.map(contactPointSnapshot)

  if (contactPoints.some((point) => point === null)) return null

  return {
    customerId,
    displayName,
    role: textValue(snapshot.role),
    contactPoints: contactPoints as WrongRecipientMutationContactPoint[],
  }
}

export const normalizeWrongRecipientReplacementOutput = (
  value: unknown,
): NormalizedWrongRecipientReplacementOutput | null => {
  const output = objectValue(value)

  if (!output) return null

  const action = textValue(output.action)

  if (action !== "replace" && action !== "remove") return null

  const affectedCustomer = customerSnapshot(output.affected_customer_snapshot)

  if (!affectedCustomer) return null

  const replacementCustomer = customerSnapshot(output.replacement_customer_snapshot)

  if (action === "replace" && !replacementCustomer) return null

  if (action === "remove" && replacementCustomer) return null

  return { action, affectedCustomer, replacementCustomer }
}

export const mutationContactValue = (
  customer: WrongRecipientMutationCustomer | null,
  kinds: string[],
): string => {
  const acceptedKinds = new Set(kinds.map((kind) => kind.toUpperCase()))

  return customer?.contactPoints.find((point) => acceptedKinds.has(point.kind))?.value ?? ""
}

export const mutationContactPointsForTag = (customer: WrongRecipientMutationCustomer | null) =>
  customer?.contactPoints.map((point) => ({
    id: point.contactPointId,
    customer_id: point.customerId,
    kind: point.kind,
    value: point.value,
    lifecycle_status: "ACTIVE",
  })) ?? []
